import { ConfigurationSourceDataMap, Metadata, Participant, SourceData, SourceDataEventContext, SourceDataEventType, SourceDataFieldType, SourceDataFormContext, SourceDataItem, SourceDataItemGroupType, SourceDataPoint, SourceDataReportItem, SourceDataReportType, Trial } from "@/api/dtos";
import { useEffect, useState } from "react";
import { ICtcaeIdentifier, getCtcae } from "./useCtcae";
import { createEventConfiguration } from "../form/configuration/useEventConfiguration";
import { createFormConfiguration } from "../form/configuration/useFormConfiguration";
import { createItemGroupConfiguration } from "../form/configuration/useItemGroupConfiguration";
import { createItem } from "../form/source-data/useItem";
import { AppSettings } from "@/AppSettings";
import { DateTime } from "luxon";

export interface ISourceDataExportRow {
    participantStudyNumber: string;
    studyEvent: string;
    caseReportForm: string;
    itemGroup?: string;
    item: string;
    value?: string;
    state: string;
    date?: string;
    user: string;
}

export interface ISourceDataExportItem {
    studyEvent: ReturnType<typeof createEventConfiguration>;
    caseReportForm: ReturnType<typeof createFormConfiguration>;
    itemGroup: ReturnType<typeof createItemGroupConfiguration>;
    itemData: ReturnType<typeof createItem>;
    row: ISourceDataExportRow;
}

const useSourceDataExport = (
    metadata?: Metadata,
    trialConfiguration?: ConfigurationSourceDataMap,
    trial?: Trial,
    participants?: Record<number, Participant>,
    eventMap?: Record<number, SourceDataEventContext>,
    formMap?: Record<number, SourceDataFormContext>,
    pointMap?: Record<number, SourceDataPoint>,
    items?: SourceDataReportItem[]
) => {
    const [exportData, setExportData] = useState<ISourceDataExportItem[]>([]);

    useEffect(() => {
        const processExport = async () => {
            if (!items || items.length === 0) {
                setExportData([]);
                return;
            }

            const data: ISourceDataExportItem[] = [];

            for (let item of items) {
                const point = pointMap?.[item.sourceDataPointId!];
                const form = formMap?.[point?.sourceDataFormContextId!];
                const event = eventMap?.[form?.sourceDataEventContextId!];
                const participant = participants?.[event?.participantId!]

                const studyEvent = createEventConfiguration(
                    metadata,
                    trialConfiguration?.events,
                    trialConfiguration?.events,
                    trial,
                    trialConfiguration?.configuration?.isGlobal,
                    trialConfiguration?.configuration?.id,
                    event?.studyEventOid
                );

                const caseReportForm = createFormConfiguration(
                    metadata,
                    trialConfiguration?.forms,
                    trialConfiguration?.forms,
                    trial,
                    trialConfiguration?.configuration?.isGlobal,
                    trialConfiguration?.configuration?.id,
                    event?.studyEventOid,
                    form?.caseReportFormOid
                );

                const itemGroup = createItemGroupConfiguration(
                    metadata,
                    trialConfiguration?.itemGroups,
                    trialConfiguration?.itemGroups,
                    trial,
                    trialConfiguration?.configuration?.isGlobal,
                    trialConfiguration?.configuration?.id,
                    event?.studyEventOid,
                    form?.caseReportFormOid,
                    form?.caseReportFormVersionOid,
                    point?.itemGroupName
                );
                
                const itemData = createItem(
                    metadata,
                    trialConfiguration?.items,
                    trialConfiguration?.items,
                    {
                        [event?.studyEventOid!]: {
                            [event?.studyEventRepeat!]: {
                                [form?.caseReportFormOid!]: {
                                    [point?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
                                        [point?.itemGroupRepeat!]: {
                                            [point?.itemName!]: new SourceDataItem({
                                                value: item.value
                                            })
                                        }
                                    }
                                }
                            }
                        }
                    },
                    trialConfiguration?.itemValues,
                    trialConfiguration?.itemValues,
                    trial,
                    undefined,
                    undefined,
                    undefined,
                    participant,
                    trialConfiguration?.configuration?.isGlobal,
                    trialConfiguration?.configuration?.id,
                    event?.studyEventOid,
                    event?.studyEventRepeat,
                    form?.caseReportFormOid,
                    form?.caseReportFormVersionOid,
                    point?.itemGroupName,
                    point?.itemGroupRepeat,
                    point?.itemName
                )

                let value = undefined;

                switch (itemData?.itemMetadata.fieldType) {
                    case SourceDataFieldType.Checkbox:
                        value = itemData?.value;
                        break;

                    case SourceDataFieldType.Ctcae:
                        const ctcaeIdentifier = itemData.value ? JSON.parse(itemData.value) as ICtcaeIdentifier : undefined;
                        const ctcae = await getCtcae(ctcaeIdentifier);

                        if (!ctcae) {
                            value = undefined;
                            break;
                        }

                        if (ctcaeIdentifier?.staticTerm && !ctcae.grade) {
                            value = undefined;
                            break;
                        }

                        if (ctcaeIdentifier?.staticTerm) {
                            value = `Grade ${ctcae.grade?.name}`;
                            break;
                        }

                        value = `${ctcae.category?.name} - ${ctcae.term?.name.replace(ctcae.category.name + ' - ', '')}${ctcae.grade ? ` - Grade ${ctcae.grade.name}` : ''}`;
                        break;
                    case SourceDataFieldType.Date:
                        value = itemData?.value;
                        break;

                    case SourceDataFieldType.File:
                        value = itemData?.value;
                        break;

                    case SourceDataFieldType.MultiSelect:
                        const values = itemData?.sourceDataItem?.value?.split(',');
                
                        value = values?.map((id) => {
                            const valueDisplayName = itemData?.itemValueConfigurations?.find(x => x.itemValue === id.trim())?.displayName;

                            return valueDisplayName ?? id;
                        }).join(', ');

                    case SourceDataFieldType.Radio:
                    case SourceDataFieldType.Select:
                        value = itemData?.value;
                        break;

                    case SourceDataFieldType.Hidden:
                    case SourceDataFieldType.MultilineText:
                    case SourceDataFieldType.Numeric:
                    case SourceDataFieldType.Text:
                        value = itemData?.value;
                        break;
                }

                data.push({
                    studyEvent: studyEvent,
                    caseReportForm: caseReportForm,
                    itemGroup: itemGroup,
                    itemData: itemData,
                    row: {
                        participantStudyNumber: participant?.studyNumber!,
                        studyEvent: `${studyEvent?.displayName}${studyEvent?.eventMetadata.type === SourceDataEventType.Repeating ? ` (${event?.studyEventRepeat})` : ''}`,
                        caseReportForm: caseReportForm?.displayName!,
                        itemGroup: itemGroup ?
                            `${itemGroup.displayName}${itemGroup?.itemGroupMetadata.type === SourceDataItemGroupType.Grid ? ` (${point?.itemGroupRepeat})` : ''}` :
                            undefined,
                        item: itemData?.displayName!,
                        value: value,
                        date: item.enteredDate ? 
                            DateTime.fromISO(item.enteredDate).toFormat('dd/MM/yyyy hh:mm:ss a') : 
                            undefined,
                        state: 
                            item.type == SourceDataReportType.Reviewed ?
                                item.state ?
                                    "Reviewed" :
                                    "Not Reviewed" :
                                item.state ?
                                    "Verified" :
                                    "Not Verified",
                        user: item.enteredByLastName ? `${item.enteredByFirstName} ${item.enteredByLastName}` : item.enteredByFirstName ?? 'unknown'
                    }
                })
            }

            setExportData(data);
        }
        

        processExport();

        
    }, [
        metadata,
        trialConfiguration,
        trial,
        participants,
        eventMap,
        formMap,
        pointMap,
        items
    ]);

    return exportData;
}

export default useSourceDataExport;